import React from 'react';
import CommonPagination from '../../components/common/CommonPagination';
import PAGINATION_CONFIG from '../../config/paginationConfig';

const WorksheetWorklistGrid = ({
  items = [],
  totalCount = 0,
  pageNumber = 1,
  pageSize = PAGINATION_CONFIG.defaultPageSize,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  isLoading,
  selectedTestIds = [],
  telemetryMap = {},
  onSelectTest,
  onSelectAll,
  onOpenDetail,
  onStartWork,
  onHoldWork,
  onResumeWork,
  onCompleteWork,
  onPrintSingle,
  onViewAudit
}) => {
  const allSelected = items.length > 0 && items.every((i) => selectedTestIds.includes(i.testId));

  const formatTatBadge = (minutes) => {
    if (minutes === null || minutes === undefined) return <span style={{ color: '#94a3b8' }}>-</span>;
    if (minutes < 0) {
      return (
        <span className="ws-badge badge-tat-warning" title="TAT Breached / Overdue!">
          ⚠️ {Math.abs(minutes)}m Overdue
        </span>
      );
    }
    if (minutes <= 30) {
      return (
        <span className="ws-badge" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74' }}>
          ⏱️ {minutes}m left
        </span>
      );
    }
    return (
      <span className="ws-badge badge-routine">
        {minutes > 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'STAT':
        return <span className="ws-badge badge-stat">⚡ STAT</span>;
      case 'Urgent':
        return <span className="ws-badge badge-urgent">🔥 Urgent</span>;
      default:
        return <span className="ws-badge badge-routine">Routine</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'In Progress':
        return <span className="ws-badge badge-inprogress">● In Progress</span>;
      case 'Hold':
        return <span className="ws-badge badge-hold">⏸ On Hold</span>;
      case 'Completed':
        return <span className="ws-badge badge-completed">✓ Completed</span>;
      case 'Rejected':
        return <span className="ws-badge badge-tat-warning">✕ Rejected</span>;
      case 'Repeat':
        return <span className="ws-badge badge-urgent">↺ Repeat</span>;
      default:
        return <span className="ws-badge badge-pending">Pending</span>;
    }
  };

  const getStageBadge = (stage) => {
    switch (stage) {
      case 'NOT_COLLECTED':
        return (
          <span
            className="ws-badge"
            style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 700 }}
            title="Specimen collection pending in Phlebotomy"
          >
            🔴 Draw Pending
          </span>
        );
      case 'COLLECTED_IN_TRANSIT':
        return (
          <span
            className="ws-badge"
            style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', fontWeight: 600 }}
            title="Specimen drawn, in transit to laboratory"
          >
            🟡 In Transit
          </span>
        );
      default:
        return (
          <span
            className="ws-badge"
            style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600 }}
            title="Specimen received on laboratory bench"
          >
            🟢 In Lab
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="ws-table-card">
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '28px', marginBottom: '12px' }}>🔄</div>
          <div style={{ fontWeight: 600 }}>Loading laboratory specimens...</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>Querying active pathology worklist &amp; TAT metrics</div>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="ws-table-card">
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
          <h3 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>No pending specimens found</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px' }}>
            There are no laboratory samples matching the selected filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-table-card">
      <div className="ws-table-wrapper">
        <table className="ws-table">
          <thead>
            <tr>
              <th style={{ width: '38px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  title="Select all on this page"
                />
              </th>
              <th style={{ width: '80px' }}>Priority</th>
              <th style={{ width: '100px' }}>Sample / Barcode</th>
              <th style={{ width: '100px' }}>Lab No</th>
              <th>Patient Demographics</th>
              <th>Investigation / Tests</th>
              <th style={{ width: '150px' }}>Analyzer / Bench</th>
              <th style={{ width: '110px' }}>Sample Type</th>
              <th style={{ width: '130px' }}>Collection Time</th>
              <th style={{ width: '180px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const isSelected = selectedTestIds.includes(row.testId);
              return (
                <tr key={row.testId} className={isSelected ? 'row-selected' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectTest(row.testId)}
                    />
                  </td>
                  <td>{getPriorityBadge(row.priority)}</td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.sampleNo}</div>
                    {row.barcodeNo && row.barcodeNo !== row.sampleNo && (
                      <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                        {row.barcodeNo}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0284c7' }}>{row.labNo}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.patientName}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {row.age || '-'} / {row.gender || '-'} • UHID: {row.patientId}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span
                        className="ws-badge"
                        style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}
                      >
                        {row.testName}
                      </span>
                      {row.totalTestCount > 1 && (
                        <span
                          className="ws-badge badge-routine"
                          title="Patient has multiple ordered tests"
                        >
                          +{row.totalTestCount - 1} more
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                      {row.sectionName}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const tel = telemetryMap[row.testId];
                      if (tel) {
                        const isCrit = tel.hasCriticalAlert || tel.highestSeverity === 'CRITICAL';
                        const isReady = tel.machineStatus === 'Machine Result Ready';
                        const isReview = tel.machineStatus === 'Technician Review Required';

                        return (
                          <div>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: isCrit ? '#fee2e2' : isReady ? '#dcfce7' : isReview ? '#fef3c7' : '#eff6ff',
                              color: isCrit ? '#b91c1c' : isReady ? '#166534' : isReview ? '#b45309' : '#1d4ed8',
                              border: `1px solid ${isCrit ? '#fca5a5' : isReady ? '#86efac' : isReview ? '#fde68a' : '#bfdbfe'}`
                            }}>
                              <span>🤖 {tel.analyzerCode}</span>
                            </span>
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                              {tel.machineStatus}
                            </div>
                          </div>
                        );
                      }
                      return <span style={{ fontSize: '11px', color: '#94a3b8' }}>Manual Bench</span>;
                    })()}
                  </td>
                  <td>
                    <span className="ws-badge" style={{ background: '#f1f5f9', color: '#334155' }}>
                      {row.sampleType || 'Blood'}
                    </span>
                  </td>
                  <td>
                    {row.sampleCollectionDate ? (
                      <div style={{ fontSize: '12px', color: '#1e293b' }}>
                        <div style={{ fontWeight: 600 }}>
                          {new Date(row.sampleCollectionDate).toLocaleDateString([], { day: '2-digit', month: 'short' })}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(row.sampleCollectionDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                        Not Collected
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                      {/* Detail Drawer Button */}
                      <button
                        type="button"
                        className="ws-btn ws-btn-outline ws-btn-sm"
                        onClick={() => onOpenDetail(row.testId)}
                        title="View Detailed Work Specimen"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        👁️ Detail
                      </button>

                      {/* Print Single Button */}
                      <button
                        type="button"
                        className="ws-btn ws-btn-primary ws-btn-sm"
                        onClick={() => onPrintSingle(row.testId)}
                        title="Print / Export PDF for this specimen"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        🖨️ PDF / Print
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Standard LIMS Centralized Pagination */}
      <div className="sw-pagination-wrap" style={{ background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <CommonPagination
          page={pageNumber}
          pageSize={pageSize}
          totalRecords={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          loading={isLoading}
        />
      </div>
    </div>
  );
};

export default WorksheetWorklistGrid;
